// Shared pieces of the @-mention pickers (plain textarea + Tiptap popup).
import { initials as initialsFor, safeAvatarUrl } from "./mention_tokens.js";
import { contextKey, rememberMentionables } from "./mention_audience.js";

// GET /mentionables. Returns [] on any failure; rethrows aborts so callers
// can ignore superseded requests. Rows are remembered per context, so the
// "Will notify" line knows who can see the record without asking again.
export async function fetchMentionables(url, { q = "", contextType, contextId, signal } = {}) {
  const params = new URLSearchParams({ q });
  if (contextType && contextId) {
    params.set("context_type", contextType);
    params.set("context_id", contextId);
  }
  try {
    const response = await fetch(`${url}?${params.toString()}`, {
      headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
      credentials: "same-origin",
      signal
    });
    if (!response.ok) return [];
    const data = await response.json();
    const rows = Array.isArray(data) ? data.filter((item) => item && item.type === "user") : [];
    rememberMentionables(contextKey(contextType, contextId), rows);
    return rows;
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    return [];
  }
}

// One picker row: avatar (or initials), name and role. Built with
// textContent only — server data never goes through innerHTML.
export function buildMentionOption(item, { selected = false, id } = {}) {
  const option = document.createElement("button");
  option.type = "button";
  option.setAttribute("role", "option");
  option.setAttribute("aria-selected", selected ? "true" : "false");
  option.tabIndex = -1;
  if (id) option.id = id;
  option.className =
    "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-600";
  if (selected) option.classList.add("bg-gray-100", "dark:bg-gray-600");

  const avatarUrl = safeAvatarUrl(item.avatar_url);
  if (avatarUrl) {
    const img = document.createElement("img");
    img.src = avatarUrl;
    img.alt = "";
    img.className = "h-6 w-6 shrink-0 rounded-full object-cover";
    option.appendChild(img);
  } else {
    const badge = document.createElement("span");
    badge.setAttribute("aria-hidden", "true");
    badge.className =
      "flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-[10px] font-semibold text-primary-800 dark:bg-primary-900 dark:text-primary-200";
    badge.textContent = item.initials || initialsFor(item.name);
    option.appendChild(badge);
  }

  const name = document.createElement("span");
  name.className = "min-w-0 flex-1 truncate";
  name.textContent = item.name || "";
  option.appendChild(name);

  if (item.role_label) {
    const role = document.createElement("span");
    role.className = "ml-auto shrink-0 text-xs text-gray-500 dark:text-gray-400";
    role.textContent = item.role_label;
    option.appendChild(role);
  }
  return option;
}

// Screen-reader text for a finished search: "3 colleagues found…" or the
// no-matches copy. `messages` holds server-localized strings; resultsOther
// carries a literal "%{count}" placeholder (en/nb/sv/da only pluralize
// one/other).
export function resultsAnnouncement(count, messages = {}) {
  if (!count) return messages.noMatches || "";
  if (count === 1) return messages.resultsOne || "";
  return (messages.resultsOther || "").replace("%{count}", String(count));
}

// ---- "Deal team" shortcut ----------------------------------------------------
// With a record context, the picker's first row is a pseudo-option that
// mentions everyone on the record's deal team at once (GET /mentionables/team:
// owners, assignees, renewal owner, thread participants; the server leaves
// out the writer and anyone who can't open the record). Picking it inserts
// one ordinary mention per person, so the writer sees every name and can
// delete any of them before saving: no group token is ever stored.

export const DEAL_TEAM_ID = "deal-team";

// Heroicons "user-group" (outline), shared with shared/_mention_textarea.
export const TEAM_ICON_PATH =
  "M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 1 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 1 1 4.5 0Z";

// contextKey → Promise<rows>; one request per record per page.
const teams = new Map();

export function fetchDealTeam(url, { contextType, contextId } = {}) {
  if (!url || !contextType || !contextId) return Promise.resolve([]);
  const key = contextKey(contextType, contextId);
  if (teams.has(key)) return teams.get(key);

  const params = new URLSearchParams({ context_type: contextType, context_id: contextId });
  const request = fetch(`${url}?${params.toString()}`, {
    headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
    credentials: "same-origin"
  })
    .then((response) => (response.ok ? response.json() : Promise.reject(new Error("team"))))
    .then((data) => {
      const rows = Array.isArray(data) ? data.filter((item) => item && item.type === "user" && item.id) : [];
      rememberMentionables(key, rows);
      return rows;
    })
    .catch(() => {
      // Not cached: a later "@" may try again.
      teams.delete(key);
      return [];
    });
  teams.set(key, request);
  return request;
}

// Test hook.
export function clearDealTeamCache() {
  teams.clear();
}

function normalize(text) {
  return String(text ?? "")
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "");
}

// Does the typed "@query" ask for the deal team? A bare "@" does; otherwise
// the query must start a word of the localized label, the label run together
// ("dealt" → "Deal team") or one of the localized keywords ("team").
export function matchesDealTeam(query, messages = {}) {
  const q = normalize(query);
  if (!q) return true;
  const label = String(messages.dealTeam || "Deal team");
  const words = [label, ...label.split(/\s+/), ...String(messages.dealTeamKeywords || "").split(/\s+/)];
  return words.some((word) => {
    const candidate = normalize(word);
    return candidate && candidate.startsWith(q);
  });
}

function countLabel(count, messages) {
  const template = count === 1 ? messages.dealTeamCountOne : messages.dealTeamCountOther;
  return String(template || "%{count}").replace("%{count}", String(count));
}

// The pseudo-option for a query, or null when there is nobody left to add or
// the query isn't asking for it. `exclude` holds ids already mentioned in the
// text and the writer's own id.
export function dealTeamItem(members, { query = "", exclude = [], messages = {} } = {}) {
  if (!matchesDealTeam(query, messages)) return null;
  const skip = new Set([...exclude].filter(Boolean).map((id) => String(id).toLowerCase()));
  const seen = new Set();
  const people = [];
  for (const member of members || []) {
    const id = String(member?.id || "").toLowerCase();
    const name = String(member?.name || "").trim();
    if (!id || !name || skip.has(id) || seen.has(id)) continue;
    seen.add(id);
    people.push({ ...member, id, name });
  }
  if (!people.length) return null;

  const names = people.map((person) => person.name).join(", ");
  const label = messages.dealTeam || "Deal team";
  return {
    id: DEAL_TEAM_ID,
    kind: "team",
    type: "team",
    name: label,
    label,
    members: people,
    count: people.length,
    countLabel: countLabel(people.length, messages),
    names,
    // Read out when the team row is the only option. Not an aria-label: the
    // row's visible text (label, names, count) is its accessible name.
    summary: String(messages.dealTeamOption || "%{names}").replace("%{names}", () => names)
  };
}

// Screen-reader text for a list that may start with the deal-team option.
// When it is the only option it is already active, so it is read out as such;
// otherwise the people count is followed by a pointer to it (ArrowUp).
export function pickerAnnouncement(team, peopleCount, messages = {}) {
  if (!team) return resultsAnnouncement(peopleCount, messages);
  if (!peopleCount) return team.summary;
  const template = team.count === 1 ? messages.dealTeamAvailableOne : messages.dealTeamAvailableOther;
  const pointer = String(template || "").replace("%{count}", String(team.count));
  return [resultsAnnouncement(peopleCount, messages), pointer].filter(Boolean).join(" ");
}

// The one-line confirmation read out after a deal-team pick.
export function dealTeamInsertedAnnouncement(team, messages = {}) {
  if (!team) return "";
  return String(messages.dealTeamInserted || "%{names}").replace("%{names}", () => team.names);
}

// The deal-team row for the Tiptap popup: a group icon, the label, the count,
// and the names it will insert. Built with textContent only.
export function buildDealTeamOption(item, { selected = false, id } = {}) {
  const option = document.createElement("button");
  option.type = "button";
  option.setAttribute("role", "option");
  option.setAttribute("aria-selected", selected ? "true" : "false");
  option.dataset.mentionDealTeam = "";
  option.tabIndex = -1;
  if (id) option.id = id;
  option.className =
    "flex w-full items-center gap-2 border-b border-gray-100 px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-100 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600";
  if (selected) option.classList.add("bg-gray-100", "dark:bg-gray-600");

  const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  icon.setAttribute("aria-hidden", "true");
  icon.setAttribute("viewBox", "0 0 24 24");
  icon.setAttribute("fill", "none");
  icon.setAttribute("stroke", "currentColor");
  icon.setAttribute("stroke-width", "2");
  icon.setAttribute(
    "class",
    "h-6 w-6 shrink-0 rounded-full bg-primary-100 p-1 text-primary-800 dark:bg-primary-900 dark:text-primary-200"
  );
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("stroke-linecap", "round");
  path.setAttribute("stroke-linejoin", "round");
  path.setAttribute("d", TEAM_ICON_PATH);
  icon.appendChild(path);
  option.appendChild(icon);

  const text = document.createElement("span");
  text.className = "flex min-w-0 flex-1 flex-col";
  const label = document.createElement("span");
  label.className = "font-semibold";
  label.textContent = item.name;
  const names = document.createElement("span");
  names.className = "truncate text-xs text-gray-600 dark:text-gray-300";
  names.textContent = item.names;
  text.appendChild(label);
  text.appendChild(names);
  option.appendChild(text);

  const count = document.createElement("span");
  count.className = "ml-auto shrink-0 text-xs text-gray-600 dark:text-gray-300";
  count.textContent = item.countLabel;
  option.appendChild(count);
  return option;
}
